import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import "./infinite-scroll.css";

interface InfiniteScrollProps {
  items: any[];
  itemRender: (index: number, item: any) => JSX.Element;
  loadMore: () => Promise<any>;
  hasMore: boolean;
  isLoading?: boolean;
  reverseOrder?: boolean;
  className?: string;
}

export function InfiniteScroll({
  items,
  itemRender,
  loadMore,
  hasMore,
  isLoading = false,
  reverseOrder = true,
  className,
}: InfiniteScrollProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Intersection Observer 설정
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, loadMore]);

  // 스크롤 이벤트 리스너 등록.
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    // 자석 효과를 위한 임계값 설정
    const NEAR_BOTTOM_THRESHOLD = 100;

    const handleScroll = () => {
      const scrollPos = Math.abs(scrollContainer.scrollTop);

      // console.log("scrollPos:", scrollPos);

      // 임계값을 넘으면 버튼 표시
      setShowScrollTop(scrollPos > NEAR_BOTTOM_THRESHOLD);

      // 스크롤 타이머 삭제.
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

      // 자석 효과: 임계값이하이면서, 사용자가 스크롤을 멈췄을 때
      if (scrollPos <= NEAR_BOTTOM_THRESHOLD && scrollPos > 0) {
        // 스크롤 타이머로 사용자가 스크롤을 멈췄는지 감지
        scrollTimeoutRef.current = setTimeout(() => {
          // 자동으로 최상단으로 스크롤
          scrollToTop();
        }, 300); // 300ms 동안 스크롤이 없으면 자석 효과 적용
      }
    };
    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
        // behavior: "auto",
      });
    }
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      <div
        ref={scrollRef}
        className={cn(
          "relative flex w-full h-full overflow-x-hidden overflow-y-auto custom-scrollbar",
          reverseOrder ? "flex-col-reverse" : "flex-col",
          className,
        )}
      >
        {/* Items */}
        {/* {items.map((item, index) => (
          <div key={item._id || item.id || index} className="max-w-full">
            {itemRender(item)}
          </div>
        ))} */}
        {items.map((item, index) => itemRender(index, item))}

        {/* No More Content 메시지 */}
        {!hasMore && items.length > 0 && (
          <div className="text-center p-2 text-sm text-muted-foreground">
            No more content
          </div>
        )}

        {/* Loading Indicator */}
        {isLoading && (
          <div className="flex justify-center p-2">
            <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        )}

        {/* Intersection Observer Target */}
        {hasMore && <div ref={observerRef} className="h-4" />}
      </div>
      {showScrollTop && (
        <div
          className={cn(
            "absolute left-0 right-0 flex justify-center z-10",
            reverseOrder ? "bottom-4" : "top-4",
          )}
        >
          <Button
            variant="secondary"
            size="icon"
            className="rounded-full shadow-lg"
            onClick={scrollToTop}
          >
            {reverseOrder ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronUp className="h-4 w-4" />
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
